import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import type { PublicBrandingData } from "@/features/auth/usePublicBranding";

/**
 * Fetch public branding for an organization by its custom domain.
 *
 * Used by ThemeProvider when the browser is on a custom domain portal
 * (e.g. iot.customer.example) to resolve branding before authentication.
 * Only enabled when a domain hostname is provided.
 */
export function useCustomDomainBranding(domain: string | undefined) {
  return useQuery<PublicBrandingData>({
    queryKey: ["public-branding-by-domain", domain],
    queryFn: async () => {
      const { data: envelope } = await api.get<
        { status: string; data: PublicBrandingData } | PublicBrandingData
      >(`/organizations/public-branding-by-domain/${domain}/`);

      // Handle envelope response
      if (
        "data" in envelope &&
        "status" in (envelope as Record<string, unknown>)
      ) {
        return (envelope as { data: PublicBrandingData }).data;
      }
      return envelope as PublicBrandingData;
    },
    enabled: !!domain,
    staleTime: 5 * 60 * 1000, // 5 min — matches backend cache
    retry: false, // Don't retry on 404
  });
}
